import { Box, HStack, VStack } from '@chakra-ui/react';
import { Text } from '@main/complykit/components/text';
import { curveLinear, curveMonotoneX } from '@visx/curve';
import { localPoint } from '@visx/event';
import { LinearGradient } from '@visx/gradient';
import { GridColumns, GridRows } from '@visx/grid';
import { withParentSize } from '@visx/responsive';
import {
  WithParentSizeProps,
  WithParentSizeProvidedProps,
} from '@visx/responsive/lib/enhancers/withParentSize';
import { ContinuousDomain, scaleLinear, scaleTime, TimeDomain } from '@visx/scale';
import { AreaClosed, Bar, LinePath } from '@visx/shape';
import { defaultStyles, TooltipWithBounds, withTooltip } from '@visx/tooltip';
import { WithTooltipProvidedProps } from '@visx/tooltip/lib/enhancers/withTooltip';
import { bisector, extent, max } from '@visx/vendor/d3-array';
import { CSSProperties, MouseEvent, ReactNode, TouchEvent, useCallback, useMemo } from 'react';

import { useChartScaleColors, useChartStructureColors } from '../status-tag/chart-colors';

export enum ChartScaleType {
  Linear = 'linear',
  Time = 'time',
}

export enum ChartCurveType {
  Linear = 'linear',
  Smooth = 'smooth',
}

export interface AreaChartProps<T extends object> {
  data: T[];
  xAccessor: ChartAccessor<T>;
  yAccessor: ChartAccessor<T>;
  curveType?: ChartCurveType;
  tooltip?: ChartDataAccessor<T, ReactNode>;
  noTooltip?: boolean;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  tooltipStyles?: CSSProperties;
  backgroundColor1?: string;
  backgroundColor2?: string;
  lineColor?: string;
  axisStyles?: CSSProperties;
  noAxis?: boolean;
  borderRadius?: number;
  verticalGridColor?: string;
  horizontalGridColor?: string;
}

export const AreaChart = withParentSize(withTooltip(AreaChartComponent)) as unknown as <
  T extends object,
>(
  props: AreaChartProps<T>,
) => JSX.Element;

function AreaChartComponent<T extends object>({
  data,
  xAccessor,
  yAccessor,
  curveType,
  tooltip,
  noTooltip,
  parentWidth = 0,
  parentHeight = 0,
  margin = {},
  showTooltip,
  hideTooltip,
  tooltipStyles,
  tooltipData,
  tooltipTop = 0,
  tooltipLeft = 0,
  borderRadius = 14,
  backgroundColor1 = 'transparent',
  backgroundColor2 = 'transparent',
  lineColor: lineColorProp,
  axisStyles,
  noAxis,
  verticalGridColor = 'transparent',
  horizontalGridColor: horizontalGridColorProp,
}: AreaChartProps<T> &
  WithTooltipProvidedProps<T> &
  WithParentSizeProvidedProps &
  WithParentSizeProps) {
  const {
    top: marginTop = 0,
    right: marginRight = 0,
    bottom: marginBottom = 0,
    left: marginLeft = 0,
  } = margin;
  const axisHeight = axisStyles?.height ? parseInt(String(axisStyles.height), 10) : 18;
  const width = Math.max(0, parentWidth ?? 0);
  const height = Math.max(0, (parentHeight ?? 0) - axisHeight);
  const structure = useChartStructureColors();
  const axisColor = structure.axisLabel;
  const horizontalGridColor = horizontalGridColorProp ?? structure.grid;
  /* The default series stroke reads the purple scale's solid step 9 through
   * the chart palette (was the Chakra purple.600 hex): thin strokes keep the
   * solid step for weight while area fills went soft. */
  const { stroke: defaultLineColor } = useChartScaleColors('purple');
  const lineColor = lineColorProp ?? defaultLineColor;

  const fullTooltipStyles = useMemo(
    () => ({
      ...defaultStyles,
      background: 'rgba(0, 0, 0, 0.64)',
      borderRadius: '8px',
      color: 'white',
      fontFamily: 'Inter, arial, sans-serif',
      fontSize: '10px',
      fontWeight: 400,
      ...tooltipStyles,
    }),
    [tooltipStyles],
  );

  const curve = useMemo(
    () => (curveType === ChartCurveType.Smooth ? curveMonotoneX : curveLinear),
    [curveType],
  );

  const sanitizedData = useMemo(
    () =>
      data.filter((datum) => {
        const xValue = xAccessor.accessor(datum);
        const yValue = yAccessor.accessor(datum);

        return Number.isFinite(xValue.valueOf()) && Number.isFinite(yValue.valueOf());
      }),
    [data, xAccessor, yAccessor],
  );

  // bounds
  const innerWidth = Math.max(0, width - marginLeft - marginRight);
  const innerHeight = Math.max(0, height - marginTop - marginBottom);
  const canRenderChart = innerWidth > 0 && innerHeight > 0 && sanitizedData.length > 0;

  const bisectX = useMemo(
    () => bisector((d: T) => xAccessor.accessor(d).valueOf()).left,
    [xAccessor],
  );

  const createScaleFor = useCallback(
    (accessor: ChartAccessor<T>, range: number[], linearLimit = 0) => {
      if (sanitizedData.length === 0) {
        return accessor.type === ChartScaleType.Time
          ? scaleTime({ domain: [new Date(0), new Date(1)], range })
          : scaleLinear({ domain: [0, 1], range, round: true });
      }

      return accessor.type === ChartScaleType.Time
        ? scaleTime({
            domain: accessor.domain ?? (extent(sanitizedData, accessor.accessor) as [Date, Date]),
            range,
          })
        : scaleLinear({
            domain: accessor.domain ?? [
              0,
              (max(sanitizedData, accessor.accessor) || 0) + linearLimit,
            ],
            range,
            round: true,
          });
    },
    [sanitizedData],
  );

  // scales
  const xScale = useMemo(
    () => createScaleFor(xAccessor, [marginLeft, innerWidth + marginLeft]),
    [createScaleFor, innerWidth, marginLeft, xAccessor],
  );
  const yScale = useMemo(
    () => createScaleFor(yAccessor, [innerHeight + marginTop, marginTop], innerHeight * 0.1),
    [createScaleFor, innerHeight, marginTop, yAccessor],
  );

  // tooltip handler
  const handleTooltip = useCallback(
    (event: TouchEvent<SVGRectElement> | MouseEvent<SVGRectElement>) => {
      if (!canRenderChart) {
        return;
      }

      const { x = 0 } = localPoint(event) ?? {};
      const x0 = xScale.invert(x);
      const index = bisectX(sanitizedData, x0, 1);
      const d0 = sanitizedData[index - 1];
      const d1 = sanitizedData[index];
      let d = d1;
      if (d0 && d1 && xAccessor.accessor(d1)) {
        d =
          x0.valueOf() - xAccessor.accessor(d0).valueOf() >
          xAccessor.accessor(d1).valueOf() - x0.valueOf()
            ? d1
            : d0;
      }
      if (!d) {
        return;
      }

      showTooltip({
        tooltipData: d,
        tooltipLeft: xScale(xAccessor.accessor(d)),
        tooltipTop: yScale(yAccessor.accessor(d)),
      });
    },
    [canRenderChart, xScale, bisectX, sanitizedData, xAccessor, showTooltip, yScale, yAccessor],
  );

  const getTooltip = useMemo(
    () =>
      tooltip ??
      ((data: T) => (
        <VStack alignItems="end" spacing={0}>
          <Text size="inherit">
            {xAccessor.tooltip?.(xAccessor.accessor(data) as number & Date) ??
              String(xAccessor.accessor(data))}
          </Text>
          <HStack spacing={4} justifyItems="end">
            <Box w="8px" h="8px" bg={lineColor} />
            <Text size="inherit">
              {yAccessor.tooltip?.(yAccessor.accessor(data) as number & Date) ??
                String(yAccessor.accessor(data))}
            </Text>
          </HStack>
        </VStack>
      )),
    [lineColor, tooltip, xAccessor, yAccessor],
  );

  const axis = useMemo(() => {
    const firstDatum = sanitizedData[0];
    const lastDatum = sanitizedData[sanitizedData.length - 1];

    return (
      <>
        {firstDatum ? (
          <div>
            {xAccessor.tooltip?.(xAccessor.accessor(firstDatum) as number & Date) ??
              String(xAccessor.accessor(firstDatum))}
          </div>
        ) : null}
        {sanitizedData.length > 1 && lastDatum ? (
          <div>
            {xAccessor.tooltip?.(xAccessor.accessor(lastDatum) as number & Date) ??
              String(xAccessor.accessor(lastDatum))}
          </div>
        ) : null}
      </>
    );
  }, [sanitizedData, xAccessor]);

  const hasTooltip =
    Boolean(tooltipData) &&
    !noTooltip &&
    Number.isFinite(tooltipLeft) &&
    Number.isFinite(tooltipTop);
  const resolvedTooltipData = hasTooltip ? tooltipData : undefined;

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <svg width={width} height={height}>
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="url(#area-background-gradient)"
            rx={borderRadius}
          />
          <LinearGradient
            id="area-background-gradient"
            from={backgroundColor1}
            to={backgroundColor2}
          />
          <LinearGradient
            id="area-gradient"
            from={lineColor}
            to={lineColor}
            fromOpacity={0.12}
            toOpacity={0}
          />
          {canRenderChart ? (
            <>
              <GridRows
                left={marginLeft}
                scale={yScale}
                width={innerWidth}
                strokeDasharray="1,3"
                stroke={horizontalGridColor}
                pointerEvents="none"
              />
              <GridColumns
                top={marginTop}
                scale={xScale}
                height={innerHeight}
                strokeDasharray="1,3"
                stroke={verticalGridColor}
                pointerEvents="none"
              />
              <AreaClosed<T>
                data={sanitizedData}
                curve={curve}
                x={(d) => xScale(xAccessor.accessor(d))}
                y={(d) => yScale(yAccessor.accessor(d))}
                yScale={yScale}
                fill="url(#area-gradient)"
              />
              <LinePath<T>
                data={sanitizedData}
                curve={curve}
                x={(d) => xScale(xAccessor.accessor(d))}
                y={(d) => yScale(yAccessor.accessor(d))}
                stroke={lineColor}
                strokeWidth={2}
                shapeRendering="geometricPrecision"
              />
              <Bar
                x={marginLeft}
                y={marginTop}
                width={innerWidth}
                height={innerHeight}
                fill="transparent"
                rx={14}
                onTouchStart={handleTooltip}
                onTouchMove={handleTooltip}
                onMouseMove={handleTooltip}
                onMouseLeave={() => hideTooltip()}
              />
            </>
          ) : null}
          {hasTooltip ? (
            <g>
              <circle
                cx={tooltipLeft}
                cy={tooltipTop}
                r={4}
                fill={lineColor}
                pointerEvents="none"
              />
            </g>
          ) : null}
        </svg>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            color: axisColor,
            fontFamily: 'Inter, arial, sans-serif',
            fontSize: '12px',
            fontWeight: 400,
            height: axisHeight,
            ...axisStyles,
          }}
        >
          {!noAxis && axis}
        </div>
      </div>
      {resolvedTooltipData ? (
        <TooltipWithBounds
          top={tooltipTop > innerHeight + marginTop - 50 ? tooltipTop - 50 : tooltipTop}
          left={tooltipLeft}
          style={fullTooltipStyles}
        >
          {getTooltip(resolvedTooltipData)}
        </TooltipWithBounds>
      ) : null}
    </div>
  );
}

export type ChartAccessor<T extends object> =
  | BaseChartAccessor<ChartScaleType.Linear, T, number>
  | BaseChartAccessor<ChartScaleType.Time, T, Date>;

export interface BaseChartAccessor<K extends ChartScaleType, T extends object, R = unknown> {
  type: K;
  accessor: ChartDataAccessor<T, R>;
  domain?: ContinuousDomain | TimeDomain;
  tooltip?: ChartDataAccessor<R, ReactNode>;
}

export type ChartDataAccessor<T, R = unknown> = (data: T) => R;
